import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { HoldingForm, HoldingFormValues } from "../../components/holding-form";
import { Modal } from "../../components/modal";
import {
  formValuesToResidencyName,
  residencyNameToFormValues,
} from "../../components/residency-name-input";
import { useAdditionalFields } from "../../hooks/addition-fields";
import { useGetHolding } from "../../queries/holding";
import {
  parseAdditionalFieldToFormValue,
  parseFormValuesToAdditionalFields,
} from "../../services/additional-fields";
import { updateHolding } from "../../services/holding";
import { formatDate } from "../../utils/date";
import { navigateBack } from "../../utils/navigate-back";
import { recordIdToId } from "../../utils/record-id-to-id";

export function HoldingEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("Holding ID is required");
  }

  const { data: holding, isLoading: isLoadingHolding } = useGetHolding(id);

  const { data: additionalFields } = useAdditionalFields("holding");
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [formValues, setFormValues] = useState<HoldingFormValues>({
    apartmentId: "",
    fromDate: "",
    toDate: "",
    useCustomName: false,
    customName: "",
    owners: [],
    additionalFields: additionalFields.reduce(
      (acc, field) => ({
        ...acc,
        [field.name]: "",
      }),
      {},
    ),
  });

  const isLoading = isLoadingHolding || loading;

  useEffect(() => {
    if (holding) {
      setFormValues({
        apartmentId: holding.apartment.id,
        fromDate: formatDate(holding.fromDate),
        toDate: holding.toDate ? formatDate(holding.toDate) : "",
        ...residencyNameToFormValues(holding.customName),
        owners:
          holding?.owners
            .map((owner) => ({ ...owner, share: owner.share * 100 }))
            .map((owner) => {
              if ("person" in owner) {
                return {
                  personId: owner.person.id,
                  primary: owner.primary,
                  share: owner.share,
                };
              } else if ("organization" in owner) {
                return {
                  organizationId: owner.organization.id,
                  primary: owner.primary,
                  share: owner.share,
                };
              } else {
                throw new Error("Invalid owner");
              }
            }) || [],
        additionalFields: additionalFields.reduce(
          (acc, field) => ({
            ...acc,
            [field.name]: parseAdditionalFieldToFormValue(
              holding?.additionalData?.[field.name],
            ),
          }),
          {},
        ),
      });
    }
  }, [holding, additionalFields]);

  const closeModal = () => {
    navigateBack(navigate, "/admin/register/holding");
  };

  return (
    <Modal open onClose={() => closeModal()}>
      <HoldingForm
        hasError={hasError}
        loading={isLoading}
        values={formValues}
        onCancel={() => closeModal()}
        onChange={setFormValues}
        onSubmit={() => {
          (async () => {
            setLoading(true);
            try {
              await updateHolding(id, {
                apartmentId: formValues.apartmentId,
                fromDate: new Date(formValues.fromDate),
                toDate: formValues.toDate
                  ? new Date(formValues.toDate)
                  : undefined,
                customName: formValuesToResidencyName(formValues),
                owners: formValues.owners
                  .map((owner) => ({ ...owner, share: owner.share / 100 }))
                  .map((owner) => {
                    if ("personId" in owner) {
                      return {
                        personId: owner.personId,
                        primary: owner.primary,
                        share: owner.share,
                      };
                    } else if ("organizationId" in owner) {
                      return {
                        organizationId: owner.organizationId,
                        primary: owner.primary,
                        share: owner.share,
                      };
                    } else {
                      throw new Error("Invalid owner");
                    }
                  }),
                ...(formValues.additionalFields && {
                  additionalData: parseFormValuesToAdditionalFields(
                    formValues.additionalFields,
                    additionalFields,
                    true,
                  ),
                }),
              });
              Promise.all([
                queryClient.invalidateQueries({
                  queryKey: ["holding", id],
                }),
                queryClient.invalidateQueries({
                  queryKey: ["holdings"],
                }),
                queryClient.invalidateQueries({
                  queryKey: ["apartments"],
                }),
                queryClient.invalidateQueries({
                  queryKey: ["apartment", recordIdToId(formValues.apartmentId)],
                }),
              ]);
              closeModal();
            } catch (error: unknown) {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
